import * as React from 'react';
import * as TinyMCE from 'react-tinymce';
import { OutputReferenceHelper, IdHelper, ModalHelper, EOutputLookupContext, SetOutputMethodType } from '../../helpers';
import { IDropdownOption, Label, TextField } from 'office-ui-fabric-react';
import '../../plugins/output-reference/plugin';

interface IProps {
	value: string;
	richText: boolean;
	outputOptions: IDropdownOption[];
	label?: string;
	singleLine?: boolean;
	tabIndex?: number;
	required?: boolean;
	readonly: boolean;
	outputLookupContext: EOutputLookupContext;
	onBlur: (value: string) => void;
	updateFieldName?: () => void;
	isEntityProperty?: boolean;
	autoFocus?: boolean;
}

export class ContentEditableTinyMCE extends React.Component<IProps, any> {

	constructor(props: IProps) {
		super(props);
		this.state = {
			showTinyMCE: false
		}
		this.changeShowTinyMCE = this.changeShowTinyMCE.bind(this);
	}

	changeShowTinyMCE(showTinyMCE: boolean) {
		this.setState({
			showTinyMCE: showTinyMCE,
		});

	}

	wrapperId: string;
	valueToRender: string = '';

	decodeTokens = (value: string, outputOptions: IDropdownOption[]) => {

		let outputTokenRegEx = new RegExp('##' + '[0-9,a-f,-]\*' + '##', 'gi');
		let outputIdentifierRegEx = new RegExp('##', 'g');

		value = value || '';

		let outputTokens = value.match(outputTokenRegEx);

		for (let i = 0; i < (outputTokens ? outputTokens.length : 0); i++) {

			let outputId = outputTokens[i].replace(outputIdentifierRegEx, '');
			let outputOption = outputOptions.filter(o => o.key === outputId)[0];
			if (outputOption) {

				let outputTokenElement = document.createElement('span');
				OutputReferenceHelper.fixOutputElement(outputTokenElement, outputOption.key.toString(), outputOption.text);
				value = value.replace(outputTokens[i], outputTokenElement.outerHTML);

			}

		}

		return value;
	};

	componentDidMount() {
		OutputReferenceHelper.clearOutputReferenceSearch();
		this.props.updateFieldName ? this.props.updateFieldName() : null;
	}

	componentDidUpdate() {

		//ignoreUpdateForEditor will be true if a token insert has just occurred on the editor in question
		let ignoreUpdateForEditor = OutputReferenceHelper.getFocusedEditorId() !== null;
		if (!ignoreUpdateForEditor) {
			let currentEditorValue = OutputReferenceHelper.getTinyMCEEditorValue(this.wrapperId);
			if (this.valueToRender !== currentEditorValue) {
				OutputReferenceHelper.setTinyMCEEditorContent(this.wrapperId, this.valueToRender);
			}
			OutputReferenceHelper.initialiseTinyMCEEditorFromWrapperId(this.wrapperId);
		}
	}

	focusOnWrapper() {

		setTimeout(() => {
			if (OutputReferenceHelper.getAutoFocusOnEditor()) {
				OutputReferenceHelper.focusOnTinyMCEEditor(this.wrapperId);
			}
			else {
				OutputReferenceHelper.setAutoFocusOnEditor(true);
			}
		}, 50);
	}

	render() {

		let { value, richText, outputOptions, onBlur, label, singleLine, tabIndex, required, readonly, outputLookupContext, isEntityProperty, autoFocus } = this.props;

		let markup = '';

		try {
			let content = JSON.parse(value);
			if (content.markup) {
				markup = content.markup;
			}
		} catch (e) {
			markup = '';
		}

		this.valueToRender = OutputReferenceHelper.decodeTokens(markup, outputOptions);

		this.wrapperId = this.wrapperId || IdHelper.newGuid();

		return (
			<div id={this.wrapperId}
				className={'content-editable-wrapper' + (singleLine ? ' single-line-content-editable' : '') + (required ? ' required-field' : '')}
				tabIndex={tabIndex} onFocus={() => { this.focusOnWrapper(); }} >
				{label ? <Label>{label}</Label> : null}
				{isEntityProperty ?


					this.valueToRender || this.state.showTinyMCE ?
						<TinyMCE
							content={this.valueToRender}
							config={{
								readonly: readonly,
								statusbar: false,
								extended_valid_elements: 'span[class|id|contenteditable|style|output-id|tabindex|title]',
								autoresize_bottom_margin: 0,
								autoresize_max_height: 350,
								content_css: ['/css/app.css', 'https://appsforoffice.microsoft.com/fabric/fabric-core/4.0.0/fabric.min.css'],
								content_style: singleLine ? 'html { overflow: hidden; }' : '',
								body_class: document.body.className + (singleLine ? ' single-line' : '') + (readonly ? ' is-disabled' : ''),
								menubar: richText ? true : false,
								menu: richText ? {
									edit: { title: 'Edit', items: 'undo redo | cut copy paste pastetext | selectall | searchreplace' },
									insert: { title: 'Insert', items: 'link | anchor hr charmap insertdatetime nonbreaking' },
									view: { title: 'View', items: 'preview | visualblocks visualchars visualaid | fullscreen | help' },
									format: { title: 'Format', items: 'formats | bold italic underline strikethrough superscript subscript | align | removeformat' },
									table: { title: 'Table', items: 'inserttable tableprops deletetable | cell row column' },
									tools: { title: 'Tools', items: 'code' }
								} : false,
								plugins: 'paste autoresize outputReference ' + (richText ? 'advlist anchor autolink autoresize autosave charmap code colorpicker contextmenu directionality fullpage fullscreen help hr insertdatetime link lists nonbreaking noneditable pagebreak paste preview save searchreplace tabfocus table template textcolor textpattern toc visualblocks visualchars' : ''),
								toolbar: richText ? 'undo redo | bold italic underline | styleselect | forecolor backcolor | alignleft aligncenter alignright | bullist numlist indent outdent' : false,
								contextmenu: richText ? "cut copy paste | link charmap inserttable | cell row column deletetable | code fullscreen" : false,
								ui_container: '.ms-Dialog-main'
							}}
							onInit={(event, editor) => {
								editor.plugins.paste.clipboard.pasteFormat = "text";
								editor.plugins.outputReference.outputLookupContext = outputLookupContext;
							}}
							onBlur={(event, editor) => {

								OutputReferenceHelper.setLastBlurTimeStamp();

								if (OutputReferenceHelper.getSaveOnBlur()) {
									OutputReferenceHelper.setFocusedEditorId(null);
									let editorContent = OutputReferenceHelper.encodeTokens(editor.getContent());
									let contentElement = document.createElement('div');
									contentElement.innerHTML = editorContent;
									let editorText = contentElement.innerText ? contentElement.innerText.trim() : '';
									editorText = editorText.replace(/\u200B/g, ''); //remove all zero-width spaces
									onBlur(JSON.stringify({
										text: editorText,
										markup: editorContent
									}));
									let scrollPoint = OutputReferenceHelper.getPostBlurModalScrollTop();
									if (scrollPoint) {
										ModalHelper.scrollTo(scrollPoint);
									}

									event.preventDefault();
								}

							}}
						/>
						: <Label onClick={() => this.changeShowTinyMCE(true)} >Open TinyMCE</Label>

					: <TinyMCE
						content={this.valueToRender}
						config={{
							readonly: readonly,
							statusbar: false,
							extended_valid_elements: 'span[class|id|contenteditable|style|output-id|tabindex|title]',
							autoresize_bottom_margin: 0,
							autoresize_max_height: 350,
							content_css: ['/css/app.css', 'https://appsforoffice.microsoft.com/fabric/fabric-core/4.0.0/fabric.min.css'],
							content_style: singleLine ? 'html { overflow: hidden; }' : '',
							body_class: document.body.className + (singleLine ? ' single-line' : '') + (readonly ? ' is-disabled' : ''),
							menubar: richText ? true : false,
							menu: richText ? {
								edit: { title: 'Edit', items: 'undo redo | cut copy paste pastetext | selectall | searchreplace' },
								insert: { title: 'Insert', items: 'link | anchor hr charmap insertdatetime nonbreaking' },
								view: { title: 'View', items: 'preview | visualblocks visualchars visualaid | fullscreen | help' },
								format: { title: 'Format', items: 'formats | bold italic underline strikethrough superscript subscript | align | removeformat' },
								table: { title: 'Table', items: 'inserttable tableprops deletetable | cell row column' },
								tools: { title: 'Tools', items: 'code' }
							} : false,
							plugins: 'paste autoresize outputReference ' + (richText ? 'advlist anchor autolink autoresize autosave charmap code colorpicker contextmenu directionality fullpage fullscreen help hr insertdatetime link lists nonbreaking noneditable pagebreak paste preview save searchreplace tabfocus table template textcolor textpattern toc visualblocks visualchars' : ''),
							toolbar: richText ? 'undo redo | bold italic underline | styleselect | forecolor backcolor | alignleft aligncenter alignright | bullist numlist indent outdent' : false,
							contextmenu: richText ? "cut copy paste | link charmap inserttable | cell row column deletetable | code fullscreen" : false,
							ui_container: '.ms-Dialog-main',

						}}
						init={{ auto_focus: true }}
						onInit={(event, editor) => {
							editor.plugins.paste.clipboard.pasteFormat = "text";
							editor.plugins.outputReference.outputLookupContext = outputLookupContext;
							editor.selection.select(editor.getBody(), true);
							editor.selection.collapse(false);
							if (autoFocus)
								editor.focus();
						}}
						onBlur={(event, editor) => {

							OutputReferenceHelper.setLastBlurTimeStamp();

							if (OutputReferenceHelper.getSaveOnBlur()) {
								OutputReferenceHelper.setFocusedEditorId(null);
								let editorContent = OutputReferenceHelper.encodeTokens(editor.getContent());
								let contentElement = document.createElement('div');
								contentElement.innerHTML = editorContent;
								let editorText = contentElement.innerText ? contentElement.innerText.trim() : '';
								editorText = editorText.replace(/\u200B/g, ''); //remove all zero-width spaces
								onBlur(JSON.stringify({
									text: editorText,
									markup: editorContent
								}));
								let scrollPoint = OutputReferenceHelper.getPostBlurModalScrollTop();
								if (scrollPoint) {
									ModalHelper.scrollTo(scrollPoint);
								}

								event.preventDefault();
							}

						}}
					/>


				}


			</div>
		);
	}

}


// WEBPACK FOOTER //
// ./source/ts/components/output-reference/content-editable-tinymce.tsx